import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { acquireDelegatedGraphToken } from "./utils/msalTokenService";
import PdfFactViewer, { CitationSearchProgress } from "./components/pdf/PdfFactViewer";

interface ExpandedDocumentViewProps {
  documentName: string;
  filePath?: string;
  sharepointSiteId?: string;
  sharepointDriveId?: string;
  sharepointItemId?: string;
  blob: Blob | string | null;
  factHighlights: Array<{
    id: string;
    text: string;
    pageIndex?: number | null;
    pageIndices?: number[] | null;
  }>;
  activeHighlightId: string | null;
  objectUrl: string | null;
  onClose: () => void;
  onDownload: () => void;
  onReady?: (info: { searchPlugin: unknown; pageCount: number }) => void;
  onCitationSearchStart?: (totalPages: number) => void;
  onCitationSearchProgress?: (progress: CitationSearchProgress) => void;
  onCitationSearchComplete?: (success: boolean) => void;
  searchTrigger?: number;
}

export default function ExpandedDocumentView({
  documentName,
  filePath,
  sharepointSiteId,
  sharepointDriveId,
  sharepointItemId,
  blob,
  factHighlights,
  activeHighlightId,
  objectUrl,
  onClose,
  onDownload,
  onReady,
  onCitationSearchStart,
  onCitationSearchProgress,
  onCitationSearchComplete,
  searchTrigger,
}: ExpandedDocumentViewProps) {
  const [opening, setOpening] = useState(false);
  const dialogTitleId = useId();

  const hasSharePointIds = !!(sharepointSiteId && sharepointDriveId && sharepointItemId);
  const hasDirectUrl = !!(filePath && /^https?:\/\//i.test(filePath));
  const canOpen = hasDirectUrl || hasSharePointIds;

  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleOpenDocument = async () => {
    if (hasDirectUrl) {
      window.open(filePath, "_blank", "noopener,noreferrer");
      return;
    }

    if (hasSharePointIds) {
      setOpening(true);
      try {
        const graphToken = await acquireDelegatedGraphToken();
        const graphUrl = `https://graph.microsoft.com/v1.0/sites/${sharepointSiteId}/drives/${sharepointDriveId}/items/${sharepointItemId}?$select=webUrl`;
        const res = await fetch(graphUrl, {
          headers: { Authorization: `Bearer ${graphToken}` },
        });
        if (!res.ok) throw new Error(`Graph API returned ${res.status}`);
        const data = await res.json();
        if (data.webUrl) {
          window.open(data.webUrl, "_blank", "noopener,noreferrer");
        }
      } catch (err) {
        console.error("Failed to get SharePoint web URL:", err);
      } finally {
        setOpening(false);
      }
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-white animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby={dialogTitleId}
    >
      {/* Dark grey header bar */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-gray-700">
        {/* Left: PDF icon + filename */}
        <div className="flex items-center gap-3 min-w-0">
          <DocumentIcon className="h-5 w-5 text-white flex-shrink-0" />
          <span id={dialogTitleId} className="text-white text-sm font-medium truncate">
            {documentName}
          </span>
        </div>

        {/* Right: Open + Download + Close buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {canOpen && (
            <button
              onClick={handleOpenDocument}
              disabled={opening}
              className="px-3 py-1.5 text-sm font-medium text-white bg-gray-600 rounded hover:bg-gray-500 disabled:opacity-50 transition-colors"
            >
              {opening ? "Opening..." : "Open"}
            </button>
          )}
          {objectUrl && (
            <button
              onClick={onDownload}
              className="p-1.5 text-white hover:bg-white/10 rounded transition-colors"
              title="Download"
              aria-label="Download document"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 text-white hover:bg-white/10 rounded transition-colors"
            title="Close"
            aria-label="Close document viewer"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* PDF content - full remaining viewport */}
      <div className="flex-1 overflow-hidden min-h-0">
        <PdfFactViewer
          blob={blob}
          factHighlights={factHighlights}
          activeHighlightId={activeHighlightId}
          onReady={onReady}
          onCitationSearchStart={onCitationSearchStart}
          onCitationSearchProgress={onCitationSearchProgress}
          onCitationSearchComplete={onCitationSearchComplete}
          searchTrigger={searchTrigger}
        />
      </div>
    </div>,
    document.body
  );
}
